import { useEffect, useState, useRef, useCallback, forwardRef } from 'react';
import { LinearProgress, Typography, Box, styled } from '@mui/material';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import dayjs from 'dayjs';

import Message from '../Message';
import BotMessage from '../BotMessage';
import MessageSkeleton from '../MessageSkeleton';

import DateDivider from './DateDivider';
import ErrorState from './ErrorState';

import useTicketComments from 'services/help-center/queries/useTicketComments';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';
import {
    BotMessage as BotMessageProps,
    Comment,
} from 'services/help-center/queries/useTicketComments/useTicketComments.interface';

interface MessageListProps {
    ticketId: number;
}

const INITIAL_ITEM_COUNT = 5000;

// Loading component for fetching next page
function Loading() {
    return <LinearProgress color="primary" sx={{ height: 2, opacity: 0.5 }} />;
}

const ScrollerEl = styled(Box)({
    height: '100%',
    overflowY: 'auto',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
});

const Scroller = forwardRef((props, ref: React.Ref<HTMLDivElement>) => {
    return <ScrollerEl {...props} ref={ref} />;
});

const List = forwardRef((props: any, ref: React.Ref<HTMLDivElement>) => {
    const { children, ...restProps } = props;
    return (
        <div {...restProps} ref={ref}>
            <div style={{ textAlign: 'center', marginTop: 24, marginBottom: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'cement.500' }}>
                    Your messages are secure and confidential.
                </Typography>
            </div>
            {children}
        </div>
    );
});

// Helper function to create a Message component
const createMessageItem = (comment: Comment) => <Message key={`message-${comment.id}`} comment={comment} />;

// Helper function to create a DateDivider component
const createDateDivider = (date: string, id: number) => <DateDivider key={`divider-${id}`} date={date} />;

export default function MessageList({ ticketId }: MessageListProps) {
    const isZendeskMemberProjectionEnabled = useStatsigGate(FeatureGates.ZendeskMemberProjection);
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, refetch } = useTicketComments(ticketId);
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const [items, setItems] = useState<JSX.Element[]>([]);
    const [firstItemIndex, setFirstItemIndex] = useState(INITIAL_ITEM_COUNT);
    const initialLoad = useRef(true);
    const prevItemCount = useRef(0);

    // Create message items with date dividers
    const createItems = useCallback((comments: Comment[]) => {
        return comments.reduce<JSX.Element[]>((acc, comment, index) => {
            const commentDate = dayjs(comment.created_at).format('YYYY-MM-DD');
            const prevCommentDate = index > 0 ? dayjs(comments[index - 1].created_at).format('YYYY-MM-DD') : null;

            if (commentDate !== prevCommentDate && dayjs(commentDate).isBefore(dayjs(), 'day')) {
                acc.push(createDateDivider(comment.created_at, comment.id));
            }

            acc.push(createMessageItem(comment));
            return acc;
        }, []);
    }, []);

    // Add bot message to the beginning of the list if exists
    const addBotMessage = useCallback(
        (newItems: JSX.Element[], botMessage: BotMessageProps | null) => {
            if (botMessage) {
                newItems.unshift(
                    <BotMessage
                        key="bot-message"
                        initialMessage={botMessage.initialMessage}
                        botMessage={botMessage.botMessage}
                        ticketId={ticketId}
                    />
                );
            }
        },
        [ticketId]
    );

    // Update items and handle initial load scrolling
    const updateItems = useCallback((newItems: JSX.Element[]) => {
        const newItemsCount = newItems.length - prevItemCount.current;

        if (initialLoad.current) {
            setItems(newItems);
            setTimeout(() => {
                virtuosoRef.current?.scrollToIndex({
                    index: INITIAL_ITEM_COUNT,
                    align: 'end',
                });
            }, 0);
            initialLoad.current = false;
        } else {
            setFirstItemIndex((prevIndex) => prevIndex - newItemsCount);
            setItems(newItems);
        }

        prevItemCount.current = newItems.length;
    }, []);

    // Effect to process and update items when data changes
    useEffect(() => {
        if (!data) {
            return;
        }

        const comments = data.pages.flatMap((page) => page.comments);
        const displayedComments = [...comments].reverse();
        const newItems = createItems(displayedComments);

        const botMessagePage = data.pages.find((page) => page.botMessage);
        addBotMessage(newItems, botMessagePage?.botMessage ?? null);

        updateItems(newItems);
    }, [data, createItems, addBotMessage, updateItems]);

    // Load more items when reaching the start of the list
    const loadMore = useCallback(() => {
        if (initialLoad.current) {
            return;
        }
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    // Only when nothing has loaded: a failed background refetch keeps the conversation on screen.
    if (isZendeskMemberProjectionEnabled && isError && !data) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <Virtuoso
            ref={virtuosoRef}
            data={items}
            overscan={30}
            firstItemIndex={firstItemIndex}
            initialTopMostItemIndex={INITIAL_ITEM_COUNT}
            startReached={loadMore}
            itemContent={(index, item) => item}
            followOutput={false}
            components={{
                Header: isFetchingNextPage ? Loading : undefined,
                EmptyPlaceholder: MessageSkeleton,
                ScrollSeekPlaceholder: MessageSkeleton,
                Scroller,
                List,
            }}
        />
    );
}
